// BL598 Phase 2 — `gridctl get spaces [--grid <slug>]`.
//
// kubectl-noun listing of the spaces in a grid. Reads GET /api/v2/spaces (the
// same endpoint `gridctl space list` uses); `--grid <slug>` scopes to another
// grid you belong to, otherwise the active grid is used.

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';

interface SpaceRow {
  space_id: string;
  slug: string;
  name: string;
  is_default: boolean;
  member_count: number;
}

export interface GetSpacesOptions {
  grid?: string;
  json?: boolean;
  output?: string;
}

export async function runGetSpaces(
  client: IV2Client,
  opts: GetSpacesOptions,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const { spaces } = await client.get<{ spaces: SpaceRow[] }>('/api/v2/spaces');

  out.json(() => ({ total: spaces.length, spaces }));
  out.human(() => {
    if (spaces.length === 0) return 'No spaces in this grid.';
    const lines = ['Spaces:'];
    for (const s of spaces) {
      const badge = s.is_default ? ' (default)' : '';
      lines.push(
        `  ${s.slug}${badge} — ${s.member_count} member${s.member_count === 1 ? '' : 's'}`,
      );
    }
    return lines.join('\n');
  });
}

export const getSpacesCommand = addOutputOption(
  new Command('spaces')
    .description('List spaces in a grid')
    .option('--grid <slug>', 'Grid to list (defaults to the active grid)')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (opts: GetSpacesOptions) => {
  try {
    const client = await getV2Client(opts.grid ?? undefined);
    await runGetSpaces(client, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
